import type {
  GoogleCalendarSettings,
  MailSettings,
  MailSource,
  MessagingSettings,
  TelegramSettings,
  WhatsAppSettings,
} from '@pm/schemas';
import {
  Badge,
  type BadgeTone,
  Button,
  Checkbox,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  Select,
  SkeletonRows,
} from '@pm/ui';
import { Bot, CalendarSync, Copy, Mail, MessageCircle, Send } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { settingsKeys, useMailSettings, useMessagingSettings } from '@/lib/branding';
import { useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

const SOURCE: Record<MailSource, { tone: BadgeTone; label: string; hint: string }> = {
  PANEL: {
    tone: 'success',
    label: 'Ativo pelo painel',
    hint: 'Os e-mails saem com a configuração abaixo.',
  },
  ENVIRONMENT: {
    tone: 'info',
    label: 'Ativo pelo servidor',
    hint: 'Os e-mails saem com a configuração das variáveis de ambiente. Ative a configuração abaixo para usar a sua.',
  },
  NONE: {
    tone: 'warning',
    label: 'Desativado',
    hint: 'Nenhum e-mail é enviado: convites do portal precisam ter o link copiado e as notificações ficam só no app.',
  },
};

/** Administração → "Parâmetros": e-mail, WhatsApp, Telegram e Google Calendar da plataforma. */
export function ParametersSection() {
  const mail = useMailSettings();
  const messaging = useMessagingSettings();
  if (mail.isLoading || messaging.isLoading) return <SkeletonRows rows={8} />;
  if (mail.isError || !mail.data)
    return <ErrorState message={mail.error?.message} onRetry={() => void mail.refetch()} />;
  if (messaging.isError || !messaging.data)
    return (
      <ErrorState message={messaging.error?.message} onRetry={() => void messaging.refetch()} />
    );
  return (
    <div className="flex flex-col gap-gap-lg">
      <MailForm key={mail.data.updated_at ?? 'novo'} settings={mail.data} />
      <WhatsAppForm
        key={`wa-${messaging.data.whatsapp.updated_at ?? 'novo'}`}
        settings={messaging.data.whatsapp}
      />
      <TelegramForm
        key={`tg-${messaging.data.telegram.updated_at ?? 'novo'}`}
        settings={messaging.data.telegram}
      />
      <GoogleCalendarForm
        key={`gc-${messaging.data.google_calendar.updated_at ?? 'novo'}`}
        settings={messaging.data.google_calendar}
      />
    </div>
  );
}

interface FormState {
  enabled: boolean;
  host: string;
  port: string;
  secure: boolean;
  username: string;
  password: string;
  clearPassword: boolean;
  from_name: string;
  from_email: string;
  reply_to: string;
}

function toForm(s: MailSettings): FormState {
  return {
    enabled: s.enabled,
    host: s.host ?? '',
    port: String(s.port),
    secure: s.secure,
    username: s.username ?? '',
    password: '',
    clearPassword: false,
    from_name: s.from_name ?? '',
    from_email: s.from_email ?? '',
    reply_to: s.reply_to ?? '',
  };
}

function MailForm({ settings }: { settings: MailSettings }) {
  const toast = useToast();
  const me = useMe();
  const [form, setForm] = useState<FormState>(() => toForm(settings));
  const [testTo, setTestTo] = useState(me.user.email);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(settings));
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = useInvalidating(
    (f: FormState) =>
      api.put<MailSettings>('/organization/settings/mail', {
        enabled: f.enabled,
        host: f.host,
        port: Number(f.port) || 587,
        secure: f.secure,
        username: f.username,
        // Vazio = mantém a senha salva.
        ...(f.clearPassword ? { password: null } : f.password ? { password: f.password } : {}),
        from_name: f.from_name,
        from_email: f.from_email,
        reply_to: f.reply_to,
      }),
    [settingsKeys.mail],
  );
  const test = useInvalidating(
    (to: string) => api.post('/organization/settings/mail/test', { to }),
    [],
  );
  const source = SOURCE[settings.effective_source];

  return (
    <div className="grid items-start gap-gap-lg xl:grid-cols-[1fr_360px]">
      <form
        className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form, {
            onSuccess: () => toast.success('Parâmetros de e-mail salvos'),
            onError: (err) => toast.error(err),
          });
        }}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <LabelCaps>E-mail (SMTP)</LabelCaps>
            <p className="mt-1 text-sm text-text-3">
              Usado em convites e redefinição de acesso do portal do cliente e nas notificações por
              e-mail.
            </p>
          </div>
          <Badge tone={source.tone}>{source.label}</Badge>
        </div>
        <p className="rounded border border-border bg-surface-2 px-3 py-2 text-sm text-text-2">
          {source.hint}
        </p>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={form.enabled} onCheckedChange={(v) => set('enabled', v === true)} />
          Usar esta configuração para enviar os e-mails da plataforma
        </label>

        <fieldset
          disabled={!form.enabled}
          className="grid gap-3 disabled:opacity-60 sm:grid-cols-6"
        >
          <div className="sm:col-span-3">
            <Field label="Servidor SMTP" htmlFor="ml-host">
              <Input
                id="ml-host"
                mono
                value={form.host}
                onChange={(e) => set('host', e.target.value)}
                placeholder="smtp.seuprovedor.com"
                maxLength={200}
              />
            </Field>
          </div>
          <div className="sm:col-span-1">
            <Field label="Porta" htmlFor="ml-port">
              <Input
                id="ml-port"
                mono
                inputMode="numeric"
                value={form.port}
                onChange={(e) => set('port', e.target.value.replace(/\D/g, '').slice(0, 5))}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Segurança" htmlFor="ml-secure">
              <Select
                id="ml-secure"
                value={form.secure ? 'ssl' : 'starttls'}
                onChange={(e) => {
                  const secure = e.target.value === 'ssl';
                  setForm((f) => ({
                    ...f,
                    secure,
                    // Ajusta a porta padrão só se ela ainda é a padrão do outro modo.
                    port:
                      secure && f.port === '587'
                        ? '465'
                        : !secure && f.port === '465'
                          ? '587'
                          : f.port,
                  }));
                }}
              >
                <option value="starttls">STARTTLS</option>
                <option value="ssl">SSL/TLS</option>
              </Select>
            </Field>
          </div>
          <div className="sm:col-span-3">
            <Field label="Usuário" htmlFor="ml-user">
              <Input
                id="ml-user"
                value={form.username}
                onChange={(e) => set('username', e.target.value)}
                autoComplete="off"
                maxLength={200}
              />
            </Field>
          </div>
          <div className="sm:col-span-3">
            <Field
              label="Senha"
              htmlFor="ml-pass"
              hint={
                settings.has_password && !form.clearPassword
                  ? 'Há uma senha salva. Deixe em branco para mantê-la.'
                  : undefined
              }
            >
              <Input
                id="ml-pass"
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value, clearPassword: false }))
                }
                placeholder={settings.has_password && !form.clearPassword ? '••••••••' : ''}
                autoComplete="new-password"
                maxLength={500}
              />
            </Field>
            {settings.has_password ? (
              <label className="mt-1.5 flex items-center gap-2 text-xs text-text-2">
                <Checkbox
                  checked={form.clearPassword}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, clearPassword: v === true, password: '' }))
                  }
                />
                Remover a senha salva
              </label>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Field label="Nome do remetente" htmlFor="ml-from-name">
              <Input
                id="ml-from-name"
                value={form.from_name}
                onChange={(e) => set('from_name', e.target.value)}
                placeholder="Ex.: WI Agência"
                maxLength={120}
              />
            </Field>
          </div>
          <div className="sm:col-span-3">
            <Field label="E-mail do remetente" htmlFor="ml-from-email">
              <Input
                id="ml-from-email"
                type="email"
                value={form.from_email}
                onChange={(e) => set('from_email', e.target.value)}
                placeholder="no-reply@suaempresa.com.br"
                maxLength={254}
              />
            </Field>
          </div>
          <div className="sm:col-span-6">
            <Field
              label="Responder para (opcional)"
              htmlFor="ml-reply"
              hint="Para onde vão as respostas dos destinatários, se diferente do remetente."
            >
              <Input
                id="ml-reply"
                type="email"
                value={form.reply_to}
                onChange={(e) => set('reply_to', e.target.value)}
                maxLength={254}
              />
            </Field>
          </div>
        </fieldset>

        <div className="-mx-4 -mb-4 mt-1 flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {dirty ? (
            <Button type="button" variant="ghost" onClick={() => setForm(toForm(settings))}>
              Descartar
            </Button>
          ) : null}
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
            Salvar
          </Button>
        </div>
      </form>

      <form
        className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          test.mutate(testTo, {
            onSuccess: () =>
              toast.success('E-mail de teste enviado', `Confira a caixa de ${testTo}.`),
            onError: (err) => toast.error(err),
          });
        }}
      >
        <div className="flex items-center gap-2">
          <Mail className="size-4 text-text-2" aria-hidden />
          <LabelCaps>Testar envio</LabelCaps>
        </div>
        <p className="text-sm text-text-3">
          Envia uma mensagem usando a configuração em uso agora ({source.label.toLowerCase()}).
        </p>
        <Field label="Enviar para" htmlFor="ml-test">
          <Input
            id="ml-test"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </Field>
        {dirty ? (
          <p className="text-xs text-warning">Salve as alterações antes de testar.</p>
        ) : null}
        <Button
          type="submit"
          variant="secondary"
          loading={test.isPending}
          disabled={dirty || !testTo || settings.effective_source === 'NONE'}
        >
          <Send /> Enviar e-mail de teste
        </Button>
      </form>
    </div>
  );
}

// ---------- WhatsApp (Evolution) ----------

const CHANNEL_SOURCE: Record<MailSource, { tone: BadgeTone; label: string }> = {
  PANEL: { tone: 'success', label: 'Ativo pelo painel' },
  ENVIRONMENT: { tone: 'info', label: 'Ativo pelo servidor' },
  NONE: { tone: 'warning', label: 'Desativado' },
};

interface WhatsAppFormState {
  enabled: boolean;
  flavor: 'go' | 'api';
  api_url: string;
  api_key: string;
  clearKey: boolean;
  instance: string;
}

const toWhatsAppForm = (s: WhatsAppSettings): WhatsAppFormState => ({
  enabled: s.enabled,
  flavor: s.flavor,
  api_url: s.api_url ?? '',
  api_key: '',
  clearKey: false,
  instance: s.instance ?? '',
});

function WhatsAppForm({ settings }: { settings: WhatsAppSettings }) {
  const toast = useToast();
  const me = useMe();
  const [form, setForm] = useState(() => toWhatsAppForm(settings));
  const [number, setNumber] = useState(me.user.phone?.replace(/[^+0-9]/g, '') ?? '');
  const dirty = JSON.stringify(form) !== JSON.stringify(toWhatsAppForm(settings));
  const set = <K extends keyof WhatsAppFormState>(k: K, v: WhatsAppFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const save = useInvalidating(
    (f: WhatsAppFormState) =>
      api.put<MessagingSettings>('/organization/settings/whatsapp', {
        enabled: f.enabled,
        flavor: f.flavor,
        api_url: f.api_url,
        instance: f.instance,
        // Vazio = mantém o token salvo.
        ...(f.clearKey ? { api_key: null } : f.api_key ? { api_key: f.api_key } : {}),
      }),
    [settingsKeys.messaging],
  );
  const test = useInvalidating(
    (n: string) => api.post('/organization/settings/whatsapp/test', { number: n }),
    [],
  );
  const source = CHANNEL_SOURCE[settings.effective_source];
  const go = form.flavor === 'go';

  return (
    <div className="grid items-start gap-gap-lg xl:grid-cols-[1fr_360px]">
      <form
        className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form, {
            onSuccess: () => toast.success('Parâmetros do WhatsApp salvos'),
            onError: (err) => toast.error(err),
          });
        }}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <LabelCaps>WhatsApp (Evolution)</LabelCaps>
            <p className="mt-1 text-sm text-text-3">
              Notificações por WhatsApp para quem cadastrar o número em Configurações › Notificações
              e ativar o canal nos eventos.
            </p>
          </div>
          <Badge tone={source.tone}>{source.label}</Badge>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={form.enabled} onCheckedChange={(v) => set('enabled', v === true)} />
          Usar esta configuração para enviar as mensagens de WhatsApp
        </label>

        <fieldset
          disabled={!form.enabled}
          className="grid gap-3 disabled:opacity-60 sm:grid-cols-6"
        >
          <div className="sm:col-span-2">
            <Field label="Serviço" htmlFor="wa-flavor">
              <Select
                id="wa-flavor"
                value={form.flavor}
                onChange={(e) => set('flavor', e.target.value as 'go' | 'api')}
              >
                <option value="go">Evolution Go</option>
                <option value="api">Evolution API (v2)</option>
              </Select>
            </Field>
          </div>
          <div className="sm:col-span-4">
            <Field label="URL da API" htmlFor="wa-url">
              <Input
                id="wa-url"
                mono
                value={form.api_url}
                onChange={(e) => set('api_url', e.target.value)}
                placeholder="https://evogo.suaempresa.com.br"
                maxLength={300}
              />
            </Field>
          </div>
          <div className={go ? 'sm:col-span-6' : 'sm:col-span-3'}>
            <Field
              label={go ? 'Token da instância' : 'Chave da API (apikey)'}
              htmlFor="wa-key"
              hint={
                settings.has_api_key && !form.clearKey
                  ? 'Há um token salvo. Deixe em branco para mantê-lo.'
                  : go
                    ? 'No manager do Evolution Go, é o token da instância conectada.'
                    : undefined
              }
            >
              <Input
                id="wa-key"
                type="password"
                mono
                value={form.api_key}
                onChange={(e) =>
                  setForm((f) => ({ ...f, api_key: e.target.value, clearKey: false }))
                }
                placeholder={settings.has_api_key && !form.clearKey ? '••••••••' : ''}
                autoComplete="off"
                maxLength={500}
              />
            </Field>
            {settings.has_api_key ? (
              <label className="mt-1.5 flex items-center gap-2 text-xs text-text-2">
                <Checkbox
                  checked={form.clearKey}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, clearKey: v === true, api_key: '' }))
                  }
                />
                Remover o token salvo
              </label>
            ) : null}
          </div>
          {!go ? (
            <div className="sm:col-span-3">
              <Field label="Instância" htmlFor="wa-instance">
                <Input
                  id="wa-instance"
                  mono
                  value={form.instance}
                  onChange={(e) => set('instance', e.target.value)}
                  maxLength={120}
                />
              </Field>
            </div>
          ) : null}
        </fieldset>

        <div className="-mx-4 -mb-4 mt-1 flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {dirty ? (
            <Button type="button" variant="ghost" onClick={() => setForm(toWhatsAppForm(settings))}>
              Descartar
            </Button>
          ) : null}
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
            Salvar
          </Button>
        </div>
      </form>

      <form
        className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          test.mutate(number, {
            onSuccess: () =>
              toast.success('Mensagem de teste enviada', `Confira o WhatsApp de ${number}.`),
            onError: (err) => toast.error(err),
          });
        }}
      >
        <div className="flex items-center gap-2">
          <MessageCircle className="size-4 text-text-2" aria-hidden />
          <LabelCaps>Testar envio</LabelCaps>
        </div>
        <p className="text-sm text-text-3">
          Envia uma mensagem real pela configuração em uso agora ({source.label.toLowerCase()}).
        </p>
        <Field label="Número com DDI e DDD" htmlFor="wa-test">
          <Input
            id="wa-test"
            mono
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+5531999990000"
          />
        </Field>
        {dirty ? (
          <p className="text-xs text-warning">Salve as alterações antes de testar.</p>
        ) : null}
        <Button
          type="submit"
          variant="secondary"
          loading={test.isPending}
          disabled={dirty || !number || settings.effective_source === 'NONE'}
        >
          <Send /> Enviar mensagem de teste
        </Button>
      </form>
    </div>
  );
}

// ---------- Telegram ----------

function TelegramForm({ settings }: { settings: TelegramSettings }) {
  const toast = useToast();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [token, setToken] = useState('');
  const [clearToken, setClearToken] = useState(false);
  const dirty = enabled !== settings.enabled || Boolean(token) || clearToken;
  const save = useInvalidating(
    () =>
      api.put<MessagingSettings>('/organization/settings/telegram', {
        enabled,
        ...(clearToken ? { bot_token: null } : token ? { bot_token: token.trim() } : {}),
      }),
    [settingsKeys.messaging],
  );
  const test = useInvalidating(
    () => api.post<{ bot_username: string }>('/organization/settings/telegram/test'),
    [],
  );
  const source = CHANNEL_SOURCE[settings.effective_source];

  return (
    <div className="grid items-start gap-gap-lg xl:grid-cols-[1fr_360px]">
      <form
        className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined, {
            onSuccess: () => toast.success('Parâmetros do Telegram salvos'),
            onError: (err) => toast.error(err),
          });
        }}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <LabelCaps>Telegram</LabelCaps>
            <p className="mt-1 text-sm text-text-3">
              Crie um bot no <span className="font-mono">@BotFather</span> só para o sistema e cole
              o token. Um bot não pode ser usado por outro sistema ao mesmo tempo.
            </p>
          </div>
          <Badge tone={source.tone}>{source.label}</Badge>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(v === true)} />
          Usar este bot para enviar as mensagens de Telegram
        </label>

        <fieldset disabled={!enabled} className="flex flex-col gap-3 disabled:opacity-60">
          <Field
            label="Token do bot"
            htmlFor="tg-token"
            hint={
              settings.has_bot_token && !clearToken
                ? 'Há um token salvo. Deixe em branco para mantê-lo.'
                : 'Formato 123456789:ABC… O token é conferido no Telegram ao salvar.'
            }
          >
            <Input
              id="tg-token"
              type="password"
              mono
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setClearToken(false);
              }}
              placeholder={settings.has_bot_token && !clearToken ? '••••••••' : ''}
              autoComplete="off"
              maxLength={200}
            />
          </Field>
          {settings.has_bot_token ? (
            <label className="flex items-center gap-2 text-xs text-text-2">
              <Checkbox
                checked={clearToken}
                onCheckedChange={(v) => {
                  setClearToken(v === true);
                  setToken('');
                }}
              />
              Remover o token salvo
            </label>
          ) : null}
          <Field label="Bot" htmlFor="tg-username" hint="Preenchido automaticamente pelo Telegram.">
            <Input
              id="tg-username"
              mono
              value={settings.bot_username ? `@${settings.bot_username}` : ''}
              placeholder="—"
              disabled
            />
          </Field>
        </fieldset>

        <div className="-mx-4 -mb-4 mt-1 flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {dirty ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEnabled(settings.enabled);
                setToken('');
                setClearToken(false);
              }}
            >
              Descartar
            </Button>
          ) : null}
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
            Salvar
          </Button>
        </div>
      </form>

      <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4">
        <div className="flex items-center gap-2">
          <Bot className="size-4 text-text-2" aria-hidden />
          <LabelCaps>Testar bot</LabelCaps>
        </div>
        <p className="text-sm text-text-3">
          Confere se o bot em uso agora ({source.label.toLowerCase()}) responde. Para receber
          mensagens, cada pessoa vincula o próprio chat em Configurações › Notificações.
        </p>
        {dirty ? (
          <p className="text-xs text-warning">Salve as alterações antes de testar.</p>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          loading={test.isPending}
          disabled={dirty || settings.effective_source === 'NONE'}
          onClick={() =>
            test.mutate(undefined, {
              onSuccess: (r) => toast.success('Bot respondendo', `@${r.bot_username}`),
              onError: (err) => toast.error(err),
            })
          }
        >
          <Bot /> Testar bot
        </Button>
      </div>
    </div>
  );
}

/**
 * Google Calendar: credenciais OAuth da organização. Com elas, cada pessoa conecta a própria
 * agenda em Configurações › Notificações › Calendário externo (compromissos vão para lá).
 */
function GoogleCalendarForm({ settings }: { settings: GoogleCalendarSettings }) {
  const toast = useToast();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [clientId, setClientId] = useState(settings.client_id ?? '');
  const [secret, setSecret] = useState('');
  const [clearSecret, setClearSecret] = useState(false);
  const dirty =
    enabled !== settings.enabled ||
    clientId.trim() !== (settings.client_id ?? '') ||
    Boolean(secret) ||
    clearSecret;
  const save = useInvalidating(
    () =>
      api.put<MessagingSettings>('/organization/settings/google-calendar', {
        enabled,
        client_id: clientId.trim() || null,
        ...(clearSecret ? { client_secret: null } : secret ? { client_secret: secret.trim() } : {}),
      }),
    [settingsKeys.messaging],
  );
  const source = CHANNEL_SOURCE[settings.effective_source];

  return (
    <div className="grid items-start gap-gap-lg xl:grid-cols-[1fr_360px]">
      <form
        className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined, {
            onSuccess: () => toast.success('Parâmetros do Google Calendar salvos'),
            onError: (err) => toast.error(err),
          });
        }}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <LabelCaps>Google Calendar</LabelCaps>
            <p className="mt-1 text-sm text-text-3">
              Credenciais OAuth do Google Cloud. Com elas, cada pessoa conecta a própria agenda e os
              compromissos criados aqui vão para o Google. Entregas (prazos de demandas) ficam só no
              WIP.
            </p>
          </div>
          <Badge tone={source.tone}>{source.label}</Badge>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(v === true)} />
          Usar estas credenciais para conectar agendas do Google
        </label>

        <fieldset disabled={!enabled} className="flex flex-col gap-3 disabled:opacity-60">
          <Field
            label="ID do cliente"
            htmlFor="gc-client"
            hint="Termina em .apps.googleusercontent.com"
          >
            <Input
              id="gc-client"
              mono
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              autoComplete="off"
              maxLength={200}
            />
          </Field>
          <Field
            label="Chave secreta do cliente"
            htmlFor="gc-secret"
            hint={
              settings.has_client_secret && !clearSecret
                ? 'Há uma chave salva. Deixe em branco para mantê-la.'
                : 'Fica cifrada no banco e nunca volta para a tela.'
            }
          >
            <Input
              id="gc-secret"
              type="password"
              mono
              value={secret}
              onChange={(e) => {
                setSecret(e.target.value);
                setClearSecret(false);
              }}
              placeholder={settings.has_client_secret && !clearSecret ? '••••••••' : ''}
              autoComplete="off"
              maxLength={200}
            />
          </Field>
          {settings.has_client_secret ? (
            <label className="flex items-center gap-2 text-xs text-text-2">
              <Checkbox
                checked={clearSecret}
                onCheckedChange={(v) => {
                  setClearSecret(v === true);
                  setSecret('');
                }}
              />
              Remover a chave salva
            </label>
          ) : null}
        </fieldset>

        <div className="-mx-4 -mb-4 mt-1 flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {dirty ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEnabled(settings.enabled);
                setClientId(settings.client_id ?? '');
                setSecret('');
                setClearSecret(false);
              }}
            >
              Descartar
            </Button>
          ) : null}
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
            Salvar
          </Button>
        </div>
      </form>

      <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4">
        <div className="flex items-center gap-2">
          <CalendarSync className="size-4 text-text-2" aria-hidden />
          <LabelCaps>Como criar as credenciais</LabelCaps>
        </div>
        <ol className="flex list-decimal flex-col gap-1.5 pl-4 text-sm text-text-2">
          <li>No Google Cloud, ative a Google Calendar API no projeto.</li>
          <li>
            Em Credenciais, crie um ID do cliente OAuth do tipo Aplicativo da Web (na tela de
            consentimento, publique o app ou adicione as contas como teste).
          </li>
          <li>Em URIs de redirecionamento autorizados, cole:</li>
        </ol>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-sm bg-surface-2 px-2 py-1 font-mono text-xs text-text-1">
            {settings.redirect_uri}
          </code>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Copiar URI"
            onClick={() =>
              void navigator.clipboard
                .writeText(settings.redirect_uri)
                .then(() => toast.success('URI copiada'))
                .catch(() => toast.error(null, 'Não foi possível copiar.'))
            }
          >
            <Copy />
          </Button>
        </div>
        <p className="text-xs text-text-3">
          Depois de salvar, cada pessoa conecta a agenda em Configurações › Notificações ›
          Calendário externo.
        </p>
      </div>
    </div>
  );
}
